import React from 'react';
import KnameBackdrop from './KnameBackdrop';

const LOADING_STARS = [
  { x: '8%', y: '20%', s: 3, o: 0.6 },
  { x: '90%', y: '15%', s: 2, o: 0.5 },
  { x: '94%', y: '70%', s: 2, o: 0.4 },
  { x: '12%', y: '85%', s: 2, o: 0.45 },
  { x: '46%', y: '8%', s: 2, o: 0.35 },
  { x: '70%', y: '92%', s: 3, o: 0.5 },
];

const PHASE_LABEL = {
  assets: 'Gathering your stars',
  render: 'Painting your card',
  done: 'Almost there',
};

/**
 * 카드 렌더링이 끝날 때까지 보여주는 로딩 화면.
 *
 * @param {string} givenName
 * @param {{loaded:number,total:number,ratio:number,phase:string}} progress
 *        resultCache 가 실제 완료한 단계 수를 기준으로 올려주는 값.
 */
const KnameLoading = ({ givenName, progress }) => {
  const ratio = progress?.ratio ?? 0;
  const percent = Math.round(ratio * 100);
  const label = PHASE_LABEL[progress?.phase] ?? PHASE_LABEL.assets;

  return (
    <div className='relative flex min-h-[100svh] flex-col items-center justify-start md:justify-center overflow-hidden bg-[#17111d] px-6 py-16'>
      <KnameBackdrop stars={LOADING_STARS} glow={false} />

      {/* 헤드라인 — 결과 화면과 동일한 위치 */}
      {givenName && (
        <p
          className='relative z-10 italic text-[17px] text-[#d9b380]'
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {givenName},
        </p>
      )}
      <h1
        className='relative z-10 mt-2 text-center text-[26px] text-[#f3ede2] md:text-4xl'
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Your Korean name is on its way.
      </h1>

      {/* 카드 자리 — 스켈레톤 */}
      <div className='relative z-10 mt-10 w-[300px] md:w-[360px]'>
        <div className='relative aspect-[2/3] w-full overflow-hidden rounded-xl border border-[#d6a24b]/20 bg-[#221820] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]'>
          {/* 진행률만큼 아래에서 차오르는 골드 워시 */}
          <div
            className='absolute inset-x-0 bottom-0 transition-[height] duration-500 ease-out'
            style={{
              height: `${percent}%`,
              background:
                'linear-gradient(to top, rgba(214,162,75,0.22), rgba(214,162,75,0.04))',
            }}
          />

          <div className='absolute inset-0 flex flex-col items-center justify-center gap-4'>
            <p
              className='text-[40px] leading-none text-[#f3ede2]'
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {percent}
              <span className='ml-1 text-[18px] text-[#f2d29a]/70'>%</span>
            </p>
            <p className='px-6 text-center text-[13px] tracking-[0.15em] text-[#f2d29a]/80'>
              {label}
            </p>
          </div>
        </div>
      </div>

      {/* 진행률 바 */}
      <div className='relative z-10 mt-8 w-[300px] md:w-[360px]'>
        <div
          className='h-1 w-full overflow-hidden rounded-full bg-[#3a3140]'
          role='progressbar'
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label='Card generation progress'
        >
          <div
            className='h-full rounded-full bg-[#d6a24b] transition-[width] duration-500 ease-out'
            style={{ width: `${percent}%` }}
          />
        </div>
        {progress?.total ? (
          <p className='mt-3 text-center text-[11px] tracking-[0.2em] text-[#fff5e694]'>
            {progress.loaded} / {progress.total}
          </p>
        ) : null}
      </div>

      <p className='relative z-10 mt-5 text-center text-[12px] text-[#fff5e694]'>
        This takes a few seconds. Please keep this page open.
      </p>
    </div>
  );
};

export default KnameLoading;
